import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Alert, Spinner } from "react-bootstrap";
import { createSong, updateSong } from "../../services/songService";
import { useAuth } from "../../contexts/AuthContext";
import { useSongs } from "./SongStateProvider";
import { useNavigation } from "../../contexts/NavigationContext";
import { useLanguage } from "../../contexts/LanguageContext";
import TagInput from "./TagInput";

function SongFormModal({ show, handleClose, onSongCreated, onSongUpdated }) {
  const { t } = useLanguage();
  const { userCommunities, user } = useAuth();
  const { refreshSongs, selectedOwner, currentSong } = useSongs();
  const { isMobile } = useNavigation();

  const [title, setTitle] = useState("");
  const [composer, setComposer] = useState("");
  const [key, setKey] = useState("");
  const [tempo, setTempo] = useState("");
  const [lyrics, setLyrics] = useState("");
  const [notes, setNotes] = useState("");
  const [category, setCategory] = useState("Splošno");
  const [tags, setTags] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Initialize form with song data when editing
  useEffect(() => {
    if (currentSong) {
      setTitle(currentSong.title || "");
      setComposer(currentSong.composer || "");
      setKey(currentSong.key || "");
      setTempo(currentSong.tempo || "");
      setLyrics(currentSong.lyrics || "");
      setNotes(currentSong.notes || "");
      setCategory(currentSong.category || "Splošno");
      setTags(currentSong.tags ? currentSong.tags.map(t => t.name) : []);
    } else {
      // Reset form when creating a new song
      setTitle("");
      setComposer("");
      setKey("");
      setTempo("");
      setLyrics("");
      setNotes("");
      setCategory("Splošno");
      setTags([]);
    }
  }, [currentSong, show]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const data = {
        title,
        composer,
        key,
        tempo,
        lyrics,
        notes,
        category,
        tags,
      };

      let response;
      
      if (currentSong) {
        // Update existing song
        response = await updateSong(currentSong.id, data);
        
        if (onSongUpdated) {
          onSongUpdated(response);
        }
      } else {
        // Create new song
        // Determine community ID from selected owner in filter bar
        if (selectedOwner.startsWith('community-')) {
          const communityId = parseInt(selectedOwner.split('-')[1]);
          data.community_id = communityId;
        }
        // For "user" selection, no community_id is added (personal song)
        
        response = await createSong(data);
        
        if (onSongCreated) {
          onSongCreated(response);
        }
      }
      
      // Reset form fields
      setTitle("");
      setComposer("");
      setKey("");
      setTempo("");
      setLyrics("");
      setNotes("");
      setCategory("Splošno");
      setTags([]);

      handleClose();
    } catch (err) {
      setError(err.response?.data?.error || t('songbook.form.error'));
    } finally {
      setLoading(false);
    }
  };

  // Custom class for dark theme on mobile
  const modalClassName = isMobile ? "mobile-dark-theme" : "";
  const buttonVariant = isMobile ? "primary mobile-button" : "primary";

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      dialogClassName={modalClassName}
    >
      <Modal.Header closeButton>
        {!isMobile && <Modal.Title>{currentSong ? t('songbook.form.editSongTitle') : t('songbook.form.newSong')}</Modal.Title>}
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>{t('songbook.form.title')}</Form.Label>
            <Form.Control
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{t('songbook.form.composer')}</Form.Label>
            <Form.Control
              type="text"
              value={composer}
              onChange={(e) => setComposer(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{t('songbook.form.key')}</Form.Label>
            <Form.Control
              type="text"
              value={key}
              onChange={(e) => setKey(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{t('songbook.form.tempo')}</Form.Label>
            <Form.Control
              type="text"
              value={tempo}
              onChange={(e) => setTempo(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{t('songbook.form.lyrics')}</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={lyrics}
              onChange={(e) => setLyrics(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{t('songbook.form.notes')}</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{t('songbook.form.tags')}</Form.Label>
            <TagInput
              tags={tags}
              onChange={setTags}
              ownerType={
                currentSong
                  ? currentSong.owner_type
                  : selectedOwner.startsWith('community-')
                    ? 'community'
                    : 'user'
              }
              ownerId={
                currentSong
                  ? currentSong.owner_id
                  : selectedOwner.startsWith('community-')
                    ? parseInt(selectedOwner.split('-')[1])
                    : user?.id
              }
            />
          </Form.Group>

          {!currentSong && (
            <Form.Group className="mb-3">
              <Form.Text className="text-muted">
                {selectedOwner === 'user'
                  ? t('songbook.form.personalSong')
                  : t('songbook.form.communitySong', {
                      name: userCommunities?.find(c =>
                        `community-${c.id}` === selectedOwner
                      )?.name || ''
                    })
                }
              </Form.Text>
            </Form.Group>
          )}

          <Button variant={buttonVariant} type="submit" disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" /> : (currentSong ? t('songbook.form.updateSong') : t('songbook.form.saveSong'))}
          </Button>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

export default SongFormModal;